import {
    PurchasingContainer,
    PurchasingRegister,
} from '@Purchasing/Data/DataSource/Container'
import { PurchasingAttachment } from '@Purchasing/Domain/Model/PurchaseProcess'
import { InitiateAttachmentPayload } from '@Purchasing/Data/DataSource/API/Entity/PurchaseProcess'
import { uploadAttachmentFile } from '@/utils/firebaseStorage'
import { useCallback, useState } from 'react'

export interface UploadAttachmentInput extends InitiateAttachmentPayload {
    file: File
}

export const useAttachmentActions = () => {
    const initiateUseCase = PurchasingContainer.get(
        PurchasingRegister.UseCaseInitiateAttachment
    )
    const completeUseCase = PurchasingContainer.get(
        PurchasingRegister.UseCaseCompleteAttachment
    )
    const deleteUseCase = PurchasingContainer.get(
        PurchasingRegister.UseCaseDeleteAttachment
    )

    const [uploading, setUploading] = useState<boolean>(false)
    const [progress, setProgress] = useState<number>(0)
    const [error, setError] = useState<string | null>(null)

    /**
     * Fluxo de duas etapas (§18): backend reserva metadata + path →
     * frontend envia ao Firebase Storage → backend marca AVAILABLE.
     */
    const upload = useCallback(
        async (processId: string, input: UploadAttachmentInput) => {
            setUploading(true)
            setProgress(0)
            setError(null)
            try {
                const initiated = await initiateUseCase.execute(processId, {
                    entityType: input.entityType,
                    entityId: input.entityId,
                    category: input.category,
                    originalFileName: input.originalFileName,
                    mimeType: input.mimeType,
                    sizeBytes: input.sizeBytes,
                    description: input.description,
                })

                await uploadAttachmentFile(
                    initiated.storagePath,
                    input.file,
                    (p) => setProgress(p)
                )

                return await completeUseCase.execute(processId, initiated._id)
            } catch (e: any) {
                console.error(e)
                setError(
                    e?.response?.data?.message ?? 'Falha ao enviar o arquivo'
                )
                return null
            } finally {
                setUploading(false)
                setProgress(0)
            }
        },
        [initiateUseCase, completeUseCase]
    )

    const remove = useCallback(
        async (processId: string, attachmentId: string) => {
            setError(null)
            try {
                return await deleteUseCase.execute(processId, attachmentId)
            } catch (e: any) {
                console.error(e)
                setError(
                    e?.response?.data?.message ??
                        'Falha ao remover o documento'
                )
                return null
            }
        },
        [deleteUseCase]
    )

    return { upload, remove, uploading, progress, error }
}